import React from 'react';
import { Helmet } from 'react-helmet';
import { CONTACT_INFO } from '../constants';

function SEO({ title, description, keywords, url, breadcrumbs, faqData }) {
  const siteTitle = "BookMyNewsAd";
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const defaultDescription = "Book newspaper ads online at lowest rates. Matrimonial, Recruitment, Property, Public Notice ads in Times of India, Hindustan Times, The Hindu & more.";
  const defaultKeywords = "newspaper ad booking, book newspaper ad online, matrimonial ad, property ad, recruitment ad, public notice ad, lowest rate newspaper ad";
  const siteUrl = "https://www.bookmynewsad.com"; // Replace with actual domain when deployed
  const currentUrl = url ? `${siteUrl}${url}` : siteUrl;

  // Base Schema: Organization / LocalBusiness
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "AdvertisingAgency",
    "name": "BookMyNewsAd",
    "image": `${siteUrl}/logo192.png`,
    "@id": `${siteUrl}/#organization`,
    "url": siteUrl,
    "telephone": CONTACT_INFO.PHONE.VALUE,
    "email": CONTACT_INFO.EMAIL,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": CONTACT_INFO.ADDRESS.LINE1,
      "addressLocality": "Bangalore",
      "postalCode": "560039",
      "addressCountry": "IN"
    },
    "priceRange": "₹₹",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday"
      ],
      "opens": "00:00",
      "closes": "23:59"
    },
    "sameAs": [
      "https://www.facebook.com/bookmynewsad",
      "https://twitter.com/bookmynewsad",
      "https://www.instagram.com/bookmynewsad"
    ]
  };

  // WebSite Schema (Sitelinks Searchbox)
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "url": siteUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${siteUrl}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  // BreadcrumbList Schema
  let breadcrumbSchema = null;
  if (breadcrumbs && breadcrumbs.length > 0) {
    breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`
      }))
    };
  }

  // FAQPage Schema
  let faqSchema = null;
  if (faqData && faqData.length > 0) {
    faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
  }

  const schemas = [organizationSchema];
  if (url === '/' || url === '') schemas.push(websiteSchema);
  if (breadcrumbSchema) schemas.push(breadcrumbSchema);
  if (faqSchema) schemas.push(faqSchema);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={currentUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description || defaultDescription} />
      
      <link rel="canonical" href={currentUrl} />
      
      {/* Structured Data */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default SEO;
